import { Badge, Button, ButtonLink, Tabs } from '@artosera/ui';
import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useReglagesBoutique } from '../../api/boutique.js';
import { useConnecteurs } from '../../api/connecteurs.js';
import { useSite } from '../../api/site.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { CommandesTab } from './CommandesTab.js';
import { OeuvresEnVenteTab } from './OeuvresEnVenteTab.js';
import { ConditionsTab } from './ConditionsTab.js';
import { useAllerAOnglet } from './commun.js';
import { IndicateursBoutique } from './IndicateursBoutique.js';
import styles from './BoutiquePage.module.css';
function EtatStripe({ galerie }: { galerie: string }) {
  const { t } = useTranslation('regie');
  const connecteurs = useConnecteurs(galerie);
  const stripe = connecteurs.data?.connecteurs.find((c) => c.id === 'stripe');
  return stripe ? (
    <span className={styles.secondaire}>
      {t(
        stripe.configure
          ? stripe.mode === 'simule'
            ? 'boutique.stripeSimule'
            : 'boutique.stripePret'
          : 'boutique.stripeAbsent',
      )}
    </span>
  ) : null;
}
/** Adresse publique du site, là où les visiteurs achètent : lue dans le module site. */
function LienSite({ galerie }: { galerie: string }) {
  const { t } = useTranslation('regie');
  const site = useSite(galerie);
  const domaine = site.data?.domaine;
  return domaine ? (
    <ButtonLink
      variant="secondary"
      size="sm"
      href={`https://${domaine}`}
      target="_blank"
      rel="noreferrer"
    >
      {t('boutique.etatBoutique.voirSite', { domaine })}
      <ExternalLink size={14} strokeWidth={1.75} aria-hidden />
    </ButtonLink>
  ) : null;
}
export function BoutiquePage() {
  const { t } = useTranslation('regie');
  const { galerieSlug, galerie } = useShellContext();
  const reglages = useReglagesBoutique(galerieSlug);
  const [params, setParams] = useSearchParams();
  const allerAOnglet = useAllerAOnglet();
  const demande = params.get('onglet') ?? 'commandes';
  const onglet = ['commandes', 'oeuvres', 'conditions'].includes(demande) ? demande : 'commandes';
  const ouverte = reglages.data?.ouverte;
  return (
    <div className={styles.page}>
      <header className={styles.entete}>
        <div>
          <p className={styles.surtitre}>{t('boutique.surtitre')}</p>
          <h1>{t('boutique.titre')}</h1>
          <p className={styles.secondaire}>{t('boutique.introduction')}</p>
        </div>
        <div className={styles.statut}>
          {reglages.data && (
            <Badge tone={ouverte ? 'available' : 'neutral'}>
              {t(ouverte ? 'boutique.ouverte' : 'boutique.fermee')}
            </Badge>
          )}
          {galerieALaction(galerie, 'settings:read') && <EtatStripe galerie={galerieSlug} />}
        </div>
      </header>
      {reglages.data && (
        <section
          className={styles.etatBoutique}
          data-ouverte={ouverte ? 'true' : 'false'}
          aria-label={t('boutique.surtitre')}
        >
          <p>
            {t(ouverte ? 'boutique.etatBoutique.ouverteAide' : 'boutique.etatBoutique.fermeeAide')}
          </p>
          {ouverte ? (
            galerieALaction(galerie, 'site:read') && <LienSite galerie={galerieSlug} />
          ) : (
            <Button variant="secondary" size="sm" onClick={() => allerAOnglet('conditions')}>
              {t('boutique.etatBoutique.allerConditions')}
            </Button>
          )}
        </section>
      )}
      <IndicateursBoutique />
      <Tabs
        value={onglet}
        onValueChange={(v) => {
          const suite = new URLSearchParams(params);
          suite.set('onglet', v);
          setParams(suite);
        }}
        tabs={[
          { value: 'commandes', label: t('boutique.commandes'), content: <CommandesTab /> },
          { value: 'oeuvres', label: t('boutique.oeuvres'), content: <OeuvresEnVenteTab /> },
          { value: 'conditions', label: t('boutique.conditions'), content: <ConditionsTab /> },
        ]}
      />
    </div>
  );
}
